import React, { useCallback, useEffect, useState } from "react";
import { Container } from "reactstrap";
import { useAppDispatch, useAppSelector } from "@/services/hooks";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import ConfirmDelete from "@/components/ConfirmDelete";
import HeldSubmissionsView from "./HeldSubmissionsView";
import type { FormSubmissionListItem } from "./HeldSubmissionsView";
import {
  fetchHeldSubmissions,
  releaseSubmission,
  discardSubmission,
} from "@/services/heldSubmissionsServices/heldSubmissionsActions";

/**
 * "Held submissions" (SCRUM-1753, SCRUM-1332 slice 3): the reviewer's side of
 * the form hold queue. Lists the rows the SCRUM-1751 rules kept from SES and
 * lets an ADMINISTRATOR release (mail now) or discard each one through the
 * shared confirm dialog; the SCRUM-1752 endpoints do the rest. Success and
 * failure both reach the user through the global notification hub — the thunk
 * publishes the message, the row leaves the list only on a 2xx.
 */
const HeldSubmissionsContainer = () => {
  const dispatch = useAppDispatch();
  const { items, loading } = useAppSelector((s) => s.heldSubmissions);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [alertNode, setAlertNode] = useState<React.ReactNode>(null);

  useEffect(() => {
    dispatch(fetchHeldSubmissions());
  }, [dispatch]);

  const onToggleExpand = useCallback(
    (id: string) => setExpandedId((prev) => (prev === id ? null : id)),
    [],
  );
  const hideAlert = useCallback(() => setAlertNode(null), []);

  const confirm = useCallback(
    (title: string, thunk: ReturnType<typeof releaseSubmission>) =>
      setAlertNode(
        <ConfirmDelete
          title={title}
          showCancel
          onClose={hideAlert}
          // Rejects on failure; the error hub shows the server's reason.
          onConfirm={() => dispatch(thunk)}
        />,
      ),
    [dispatch, hideAlert],
  );

  const onRelease = useCallback(
    (row: FormSubmissionListItem) =>
      confirm(
        `Release this submission and mail it to ${row.site?.domain || "the site"}?`,
        releaseSubmission(row._id),
      ),
    [confirm],
  );
  const onDiscard = useCallback(
    (row: FormSubmissionListItem) => confirm("Discard this submission?", discardSubmission(row._id)),
    [confirm],
  );

  return (
    <>
      {alertNode}
      <SimpleHeader name="Held submissions" parentName="Publishing" />
      <Container className="mt-4" fluid>
        <HeldSubmissionsView
          items={items}
          loading={loading}
          expandedId={expandedId}
          onToggleExpand={onToggleExpand}
          onRelease={onRelease}
          onDiscard={onDiscard}
        />
      </Container>
    </>
  );
};

export default HeldSubmissionsContainer;
